import moment, { Moment } from 'moment-timezone';
import { LegFiJwtService } from '../../services/auth/legfi-jwt.service';
import { Organization } from './organization';
import { User } from './user';

export class ActivityLog
{
    id: number;
    logName: string;
    description: string;
    subjectType: string;
    event: string;
    organizationId: number;
    subjectId: number;
    causerType: string;
    causerId: number;
    properties: any;
    batchUuid: string;

    createdAt: Moment;
    organization: Organization;
    user: User;

    /**
     * SD-8943: who to show in the actor column.
     *
     * A log row with no causer is automated work — a reconciliation, a scheduled command, a queued
     * job — not an anonymous one. Showing a blank made the history unable to answer "who did this",
     * and it is why reconciliation wrongly persisted the label 'System' into the causer_type MORPH
     * column, which is what 500ed the endpoint. The label belongs here, in the display layer; the
     * column holds null or a real class. Mirrors EntityHistoryController::systemActor() (SD-7202).
     *
     * F-201a: a plain field assigned once, not a getter — the template renders it per change
     * detection cycle and the project's Angular rules forbid calling a getter from a template.
     */
    actorName: string;

    constructor(activity: any) {
        this.id = activity.id;
        this.id = activity.id;
        this.logName = activity.logName;
        this.description = activity.description;
        this.subjectType = activity.subjectType;
        this.event = activity.event;
        this.organizationId = activity.organizationId;
        this.subjectId = activity.subjectId;
        this.causerType = activity.causerType;
        this.causerId = activity.causerId;
        this.properties = activity.properties;
        this.batchUuid = activity.batchUuid;

        if (activity.organization) {
            this.organization = new Organization(activity.organization);
        }

        // F-2eb2: `includes`, NOT `?.indexOf(...) !== -1`. Optional chaining on a null causerType
        // yields `undefined`, and `undefined !== -1` is TRUE — so the guard I first wrote did the
        // exact opposite of its intent and built a named User for a row with no type at all.
        // `includes` returns undefined there, which is falsy, so a typeless row stays unattributed
        // and falls through to the System label. Core (SD-8943) can now legitimately send a null
        // causerType where it used to send the invalid literal 'System'.
        if (activity.causer && activity.causerType?.includes('User')) {
            this.user = new User(activity.causer);
        }

        // Explicit truthiness rather than `||` or `??`. The meta-lint rule prefers `??`, but `??`
        // is wrong here: it only catches null/undefined, so a causer carrying an EMPTY name would
        // render a blank actor — the exact thing this label exists to prevent.
        this.actorName = this.user?.name ? this.user.name : ActivityLog.SYSTEM_ACTOR_NAME;

        const timezone = LegFiJwtService.getTimezone();
        if (activity.createdAt) {
            this.createdAt = moment.utc(activity.createdAt, 'YYYY-MM-DD hh:mm:ss').tz(timezone);
        }
    }

    /**
     * SD-8943: who to show in the actor column.
     *
     * A log row with no causer is automated work — a reconciliation, a scheduled command, a queued
     * job — not an anonymous one. Showing a blank made the history unable to answer "who did this",
     * and it is why reconciliation wrongly persisted the label 'System' into the causer_type MORPH
     * column in the first place, which is what 500ed the endpoint. The label belongs here, in the
     * display layer; the column holds null or a real class.
     *
     * Mirrors the server-side decision in EntityHistoryController::systemActor() (SD-7202).
     */
    private static readonly SYSTEM_ACTOR_NAME = 'System';
}
